import { CircularProgress } from '@mui/material';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import { UPTanStackTable } from 'components/tanstack-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__ADD_EXCEPTION,
  CONTRACT_COMPLIANCE__DEFAULT,
  CONTRACT_COMPLIANCE__EDIT_DEFAULT,
  CONTRACT_COMPLIANCE__EMPTY_STATE,
  CONTRACT_COMPLIANCE__NO_MATCHES,
  CONTRACT_COMPLIANCE__SEARCH_SCOPE_ENTITY,
  GENERAL__RETRY,
} from 'translations/constants';
import { IRuleException } from 'types/contract-compliance/IContractCompliance';
import { PAGE_SIZE_OPTIONS } from './constants';
import { buildExceptionColumns } from './exceptionColumns';
import { UseRuleExceptionsResult } from './hooks/useRuleExceptions';
import {
  CardHeader,
  CardTitle,
  DefaultLine,
  HintText,
  PanelErrorRow,
  RuleCardContainer,
  SearchWrapper,
  ToolbarRow,
} from './styles';

interface RuleCardProps {
  title: string;
  note: string;
  defaultText?: string;
  testIdPrefix: string;
  exceptions: UseRuleExceptionsResult;
  onEditDefault: () => void;
  onAddException: () => void;
  onEditException: (row: IRuleException) => void;
  formatValue?: (row: IRuleException) => string;
}

const getRowId = (row: IRuleException) => row.id;

/** One break rule: its default value, then its own searchable, paged exception list. */
const RuleCard = ({
  title,
  note,
  defaultText,
  testIdPrefix,
  exceptions,
  onEditDefault,
  onAddException,
  onEditException,
  formatValue,
}: RuleCardProps): JSX.Element => {
  const [t] = useTranslation();
  const columns = useMemo(
    () => buildExceptionColumns(t, testIdPrefix, onEditException, formatValue),
    [t, testIdPrefix, onEditException, formatValue],
  );

  const renderExceptions = () => {
    if (exceptions.loadErrorMessage) {
      return (
        <PanelErrorRow data-testid={`${testIdPrefix}-load-error`}>
          <ErrorBar message={exceptions.loadErrorMessage} />
          <UPButton
            text={t(GENERAL__RETRY)}
            onClick={exceptions.reload}
            outlined
            testId={`${testIdPrefix}-load-retry`}
          />
        </PanelErrorRow>
      );
    }
    if (!exceptions.hasLoaded) return <CircularProgress data-testid={`${testIdPrefix}-loading`} />;
    return (
      <UPTanStackTable
        data={exceptions.rows}
        columns={columns}
        getRowId={getRowId}
        getRowTestId={row => `${testIdPrefix}-row-${row.id}`}
        testId={`${testIdPrefix}-table`}
        // Plain message inside the table, as in the mockup. A search can return nothing (and it hides Agency rows),
        // so only an unfiltered empty list gets the empty-state text.
        emptyMessage={t(
          exceptions.debouncedSearch.trim() ? CONTRACT_COMPLIANCE__NO_MATCHES : CONTRACT_COMPLIANCE__EMPTY_STATE,
        )}
        manualPagination
        pageCount={exceptions.totalPages}
        totalItems={exceptions.numberOfResults}
        pagination={exceptions.pagination}
        onPaginationChange={exceptions.setPagination}
        autoResetPageIndex={false}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        enableSorting={false}
        sx={{ height: 'auto' }}
      />
    );
  };

  return (
    <RuleCardContainer data-testid={`${testIdPrefix}-card`}>
      <CardHeader>
        <div>
          <CardTitle>{title}</CardTitle>
          <HintText>{note}</HintText>
          {defaultText && (
            <DefaultLine data-testid={`${testIdPrefix}-default`}>
              {t(CONTRACT_COMPLIANCE__DEFAULT)}: <strong>{defaultText}</strong>
            </DefaultLine>
          )}
        </div>
        <UPButton
          text={t(CONTRACT_COMPLIANCE__EDIT_DEFAULT)}
          onClick={onEditDefault}
          outlined
          disabled={!defaultText}
          testId={`${testIdPrefix}-edit-default`}
        />
      </CardHeader>
      <ToolbarRow>
        <SearchWrapper>
          <CustomSearchBox
            onSearch={exceptions.onSearch}
            placeholder={t(CONTRACT_COMPLIANCE__SEARCH_SCOPE_ENTITY)}
            testId={`${testIdPrefix}-search`}
          />
        </SearchWrapper>
        <UPButton
          text={t(CONTRACT_COMPLIANCE__ADD_EXCEPTION)}
          onClick={onAddException}
          testId={`${testIdPrefix}-add-exception`}
        />
      </ToolbarRow>
      {renderExceptions()}
    </RuleCardContainer>
  );
};

RuleCard.defaultProps = {
  defaultText: undefined,
  formatValue: undefined,
};

export default RuleCard;
